import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    Button,
    Form,
    Input,
    Select,
    Table,
    Space,
    Modal,
    Popconfirm,
    message,
    Typography,
    Card,
    Row,
    Col,
} from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    PrinterOutlined,
    PlusOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import {
    getLawyersApi,
    updateLawyerApi,
    deleteLawyerApi,
} from "../../../api/master.api";

const { Title } = Typography;
const { Option } = Select;

const COURT_OPTIONS = [
    "আপিল বিভাগ এবং হাইকোর্ট বিভাগ",
    "আপিল বিভাগ",
    "হাইকোর্ট বিভাগ",
    "ঢাকা জেলা জজ আদালত",
    "চট্রগ্রাম জেলা জজ আদালত",
    "টাঙ্গাইল জেলা জজ আদালত",
    "মুন্সিগঞ্জ জেলা জজ আদালত",
];

const LAWYER_TYPE = "প্যানেল আইনজীবী";

const PanelLawyer = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchName, setSearchName] = useState("");
    const [searchPhone, setSearchPhone] = useState("");
    const [searchCourt, setSearchCourt] = useState("");
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [editForm] = Form.useForm();

    // Convert English digits to Bengali
    const toBnDigits = (value) => {
        const en = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        const bn = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];

        let strValue = String(value);
        const num = Number(value);
        if (!isNaN(num) && num >= 1 && num <= 9) {
            strValue = strValue.padStart(2, '0');
        }

        return strValue.replace(/[0-9]/g, d => bn[en.indexOf(d)]);
    };

    // 🔥 Fetch Lawyers
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getLawyersApi(LAWYER_TYPE);
            const items = data.data || [];
            const normalized = items.map((item, idx) => ({
                key: item.lawyer_id || idx,
                ...item,
            }));
            setData(normalized);
        } catch (error) {
            message.error("ডাটা লোড করতে সমস্যা হয়েছে");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Filtered data
    const filteredEntries = useMemo(() => {
        return data
            .filter((item) => (item.lawyer_type || "") === LAWYER_TYPE)
            .filter((item) =>
                (item.lawyer_name || "")
                    .toLowerCase()
                    .includes(searchName.trim().toLowerCase())
            )
            .filter((item) =>
                (item.mobile_no || "")
                    .toLowerCase()
                    .includes(searchPhone.trim().toLowerCase())
            )
            .filter((item) =>
                searchCourt ? (item.court_name || "") === searchCourt : true
            );
    }, [data, searchCourt, searchName, searchPhone]);

    // 🔥 Update
    const handleEditSave = async (values) => {
        try {
            await updateLawyerApi(editing.lawyer_id, values);
            message.success("আপডেট সম্পন্ন");
            setIsEditModalOpen(false);
            setEditing(null);
            editForm.resetFields();
            fetchData();
        } catch (error) {
            message.error("আপডেট ব্যর্থ হয়েছে");
            console.error(error);
        }
    };

    // 🔥 Delete
    const handleDelete = async (record) => {
        try {
            await deleteLawyerApi(record.lawyer_id);
            message.success("ডিলিট সম্পন্ন");
            fetchData();
        } catch (error) {
            message.error("ডিলিট করতে ব্যর্থ");
            console.error(error);
        }
    };

    // Prepare grouped data for printing
    const groupedData = data.reduce((acc, item) => {
        if (!acc[item.court_name]) {
            acc[item.court_name] = [];
        }
        acc[item.court_name].push(item);
        return acc;
    }, {});

    const finalData = Object.keys(groupedData).map(court => ({
        court_name: court,
        lawyers: groupedData[court]
    }));

    const rows = [];
    finalData.forEach((group, groupIndex) => {
        group.lawyers.forEach((lawyer, lawyerIndex) => {
            rows.push({
                court_name: group.court_name,
                lawyer: lawyer,
                isFirstInGroup: lawyerIndex === 0,
                groupSize: group.lawyers.length,
                groupSerial: groupIndex + 1,
                lawyerIndex: lawyerIndex + 1
            });
        });
    });

    // Generate print content
    const generatePrintContent = () => {
        return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <style>
        body {
            margin: 0;
            padding: 40px;
            font-size: 16px;
            line-height: 1.6;
            font-family: 'Nikosh', 'SolaimanLipi', Arial, sans-serif;
            color: #000;
        }
        .header {
            text-align: center;
            margin-bottom: 40px;
        }
        .header-text {
            margin: 5px 0;
            font-weight: bold;
            font-size: 22px;
            text-decoration: underline;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            border-spacing: 0;
            table-layout: fixed;
        }
        th, td {
            border: 1px solid #000;
            padding: 10px;
            vertical-align: top;
            word-wrap: break-word;
        }
        th {
            background-color: #f2f2f2;
            font-weight: bold;
            text-align: center;
        }
        .serial { width: 60px; text-align: center; }
        .court-name { width: 180px; text-align: center; }
        .lawyer-info { text-align: left; }
        .lawyer-name {
            display: block;
            font-weight: bold;
            margin-bottom: 8px;
        }
        .info-row {
            margin-bottom: 5px;
        }
        @media print {
            @page { margin: 1cm; }
            body { padding: 0; }
            table, tr, td, th {
                border: 1px solid #000 !important;
            }
            .lawyer-info {
                page-break-inside: avoid;
            }
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="header-text">বাংলাদেশ সেতু কর্তৃপক্ষ নির্বাচিত প্যানেল আইনজীবীর তালিকা</div>
    </div>
    
    <table>
        <thead>
            <tr>
                <th class="serial">ক্রম</th>
                <th class="court-name">আদালতের নাম</th>
                <th>প্যানেল আইনজীবীর নাম ও ঠিকানা</th>
            </tr>
        </thead>
        <tbody>
            ${rows.map((row) => `
                <tr>
                    ${row.isFirstInGroup ? `
                        <td rowspan="${row.groupSize}" class="serial">${toBnDigits(row.groupSerial)}</td>
                        <td rowspan="${row.groupSize}" class="court-name">${row.court_name}</td>
                    ` : ''}                  
                    <td class="lawyer-info">
                        <div class="lawyer-name">${toBnDigits(row.lawyerIndex)} | ${row.lawyer.lawyer_name}</div>
                        ${row.lawyer.father_name && row.lawyer.father_name !== "undefined" ? `<div class="info-row">পিতা: ${row.lawyer.father_name}</div>` : ''}
                        ${row.lawyer.permanent_address && row.lawyer.permanent_address !== "undefined" ? `<div class="info-row">স্থায়ী ঠিকানা: ${row.lawyer.permanent_address}</div>` : ''}
                        ${row.lawyer.present_address && row.lawyer.present_address !== "undefined" ? `<div class="info-row">বর্তমান ঠিকানা: ${row.lawyer.present_address}</div>` : ''}
                        ${row.lawyer.chamber_address && row.lawyer.chamber_address !== "undefined" ? `<div class="info-row">চেম্বার ঠিকানা: ${row.lawyer.chamber_address}</div>` : ''}
                        ${row.lawyer.email && row.lawyer.email !== "undefined" ? `<div class="info-row">ইমেইল: ${row.lawyer.email}</div>` : ''}
                        ${row.lawyer.mobile_no && row.lawyer.mobile_no !== "undefined" ? `<div class="info-row">মোবাইল: ${toBnDigits(row.lawyer.mobile_no)}</div>` : ''}
                    </td>
                </tr>
            `).join('')}
        </tbody>
    </table>
</body>
</html>
`;
    };

    const handlePrint = () => {
        const printContent = generatePrintContent();
        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.onload = () => {
                setTimeout(() => {
                    printWindow.print();
                }, 500);
            };
        }
    };

    // Table Columns
    const columns = [
        {
            title: "ক্রম",
            key: "serial",
            width: "8%",
            className: "case_column_bg",
            render: (_, __, index) => <div className="text-center">{toBnDigits(index + 1)}</div>
        },
        {
            title: "আদালতের নাম",
            dataIndex: "court_name",
            key: "court_name",
            width: "25%",
            className: "case_column_bg",
        },
        {
            title: "প্যানেল আইনজীবীর নাম ও ঠিকানা",
            key: "lawyer_name",
            className: "case_column_bg",
            render: (_, record) => (
                <div>
                    <div><strong>{record.lawyer_name}</strong></div>
                    {record.father_name && record.father_name !== "undefined" && (
                        <div style={{ marginBottom: "10px" }}>পিতা: {record.father_name}</div>
                    )}
                    {record.permanent_address && record.permanent_address !== "undefined" && (
                        <div>স্থায়ী ঠিকানা: {record.permanent_address}</div>
                    )}
                    {record.present_address && record.present_address !== "undefined" && (
                        <div>বর্তমান ঠিকানা: {record.present_address}</div>
                    )}
                    {record.chamber_address && record.chamber_address !== "undefined" && (
                        <div>চেম্বার ঠিকানা: {record.chamber_address}</div>
                    )}
                    {record.email && record.email !== "undefined" && (
                        <div>ইমেইল: {record.email}</div>
                    )}
                    {record.mobile_no && record.mobile_no !== "undefined" && (
                        <div>মোবাইল: {toBnDigits(record.mobile_no)}</div>
                    )}
                </div>
            ),
        },
        {
            title: "অ্যাকশন",
            key: "action",
            width: "10%",
            fixed: "right",
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditing(record);
                            editForm.setFieldsValue(record);
                            setIsEditModalOpen(true);
                        }}
                    />
                    <Popconfirm
                        title="ডিলিট নিশ্চিত করবেন?"
                        description="আপনি কি এই প্যানেল আইনজীবীকে ডিলিট করতে চান?"
                        onConfirm={() => handleDelete(record)}
                        okText="হ্যাঁ"
                        cancelText="না"
                    >
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: "20px" }}>
            <Row justify="space-between" style={{ marginBottom: 20 }}>
                <Col>
                    <Title level={4}>প্যানেল আইনজীবীর তালিকা</Title>
                </Col>
                <Col>
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchData}
                        >
                            রিফ্রেশ
                        </Button>
                        <Button
                            type="danger"
                            icon={<PrinterOutlined />}
                            onClick={handlePrint}
                        >
                            প্রিন্ট
                        </Button>
                    </Space>
                </Col>
            </Row>

            {/* Filter Section */}
            <Card style={{ marginBottom: 20 }}>
                <Row gutter={16}>
                    <Col span={6}>
                        <Input
                            placeholder="নাম অনুসন্ধান"
                            prefix={<SearchOutlined />}
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            allowClear
                            size="large"
                        />
                    </Col>
                    <Col span={6}>
                        <Input
                            placeholder="ফোন অনুসন্ধান"
                            prefix={<SearchOutlined />}
                            value={searchPhone}
                            onChange={(e) => setSearchPhone(e.target.value)}
                            allowClear
                            size="large"
                        />
                    </Col>
                    <Col span={6}>
                        <Select
                            placeholder="কোর্ট ফিল্টার"
                            allowClear
                            value={searchCourt || undefined}
                            onChange={(v) => setSearchCourt(v || "")}
                            style={{ width: "100%" }}
                            size="large"
                        >
                            {COURT_OPTIONS.map((court) => (
                                <Option key={court} value={court}>
                                    {court}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={6}>
                        <Button
                            type="primary"
                            onClick={() => {
                                // Filter is already applied via useMemo
                            }}
                            size="large"
                        >
                            অনুসন্ধান করুন
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={filteredEntries}
                    loading={loading}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                            `${range[0]} থেকে ${range[1]} পর্যন্ত, মোট ${total} টি`,
                    }}
                    rowKey="lawyer_id"
                    bordered
                    scroll={{ x: 1024 }}
                />
            </Card>

            {/* EDIT MODAL */}
            <Modal
                title="প্যানেল আইনজীবীর তথ্য সম্পাদনা"
                open={isEditModalOpen}
                onCancel={() => {
                    setIsEditModalOpen(false);
                    setEditing(null);
                    editForm.resetFields();
                }}
                footer={null}
                width={800}
                destroyOnClose
            >
                <Form
                    form={editForm}
                    onFinish={handleEditSave}
                    layout="vertical"
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="lawyer_name"
                                label="নাম"
                                rules={[{ required: true, message: "নাম প্রয়োজন" }]}
                            >
                                <Input placeholder="প্যানেল আইনজীবীর নাম লিখুন" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="father_name" label="পিতা/স্বামী">
                                <Input placeholder="পিতা/স্বামীর নাম" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="court_category" label="কোর্ট ক্যাটাগরি">
                                <Input placeholder="ক্যাটাগরি লিখুন" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="court_name"
                                label="কোর্টের নাম"
                                rules={[{ required: true, message: "কোর্টের নাম দিন" }]}
                            >
                                <Select placeholder="কোর্ট নির্বাচন করুন" size="large" allowClear>
                                    {COURT_OPTIONS.map((court) => (
                                        <Option key={court} value={court}>
                                            {court}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="permanent_address" label="স্থায়ী ঠিকানা">
                                <Input.TextArea rows={3} placeholder="স্থায়ী ঠিকানা" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="present_address" label="বর্তমান ঠিকানা">
                                <Input.TextArea rows={3} placeholder="বর্তমান ঠিকানা" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="chamber_address" label="চেম্বারের ঠিকানা">
                                <Input.TextArea rows={3} placeholder="চেম্বারের ঠিকানা" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="email" label="ইমেইল">
                                <Input type="email" placeholder="example@email.com" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="mobile_no"
                                label="মোবাইল"
                                rules={[{ required: true, message: "মোবাইল নম্বর দিন" }]}
                            >
                                <Input placeholder="01XXXXXXXXX" size="large" maxLength={11} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="status" label="স্ট্যাটাস" initialValue="active">
                                <Select size="large">
                                    <Option value="active">Active</Option>
                                    <Option value="inactive">Inactive</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lawyer_type" label="ধরন">
                                <Input disabled className="bg-white" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                            <Button onClick={() => setIsEditModalOpen(false)}>
                                বাতিল
                            </Button>
                            <Button type="primary" htmlType="submit">
                                আপডেট করুন
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PanelLawyer;